import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { useTenantStore } from "@/stores/useTenantStore";
import {
	fetchGscSites,
	fetchTenantConnectSettings,
	saveGscConnection,
	triggerGscBackfill,
	fetchGscSyncStatus,
	GSC_ADMIN_URL,
	type GscSite,
	type GscSyncStatus,
} from "@/services/connectFlowService";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { IntegrationStatePanel } from "@/components/dashboard/IntegrationStatePanel";

type Step =
	| "intro"
	| "already_connected"
	| "picker"
	| "confirm"
	| "backfill_failed"
	| "success";

const STEP_NUMBERS: Partial<Record<Step, number>> = {
	intro: 1,
	picker: 2,
	confirm: 3,
};

/** GSC data lags ~3 days (doc 20 §2.4 — provenance for the lag constant). */
const GSC_LAG_DAYS = 3;
/** Grace beyond the lag so a healthy sync isn't flagged "short" at the edge. */
const COVERAGE_GRACE_DAYS = 2;
/** Initial backfill target (the sync route caps at 90 days). */
const BACKFILL_TARGET_DAYS = 90;
/** Auto-poll cadence while a sync is running (Norman: 30–60s, shown visibly). */
const SYNC_POLL_INTERVAL_MS = 30_000;
/** Sync statuses meaning a backfill is currently in flight. */
const SYNC_IN_PROGRESS_STATUSES = new Set(["running", "syncing", "in_progress"]);
/** Failed-sync messages that mean the agency account lacks property access. */
const GSC_AUTH_ERROR_PATTERN = /permission|forbidden|403|access[ -]?denied/i;

/**
 * Krug convention (doc 61 §C1): exactly TWO state-box patterns exist in the flow.
 * Quiet TEAL box = "nothing to do"; status-red (destructive) action box + AlertCircle =
 * "needs me" (Norman: the destructive color is the signifier, the words carry the calm;
 * coral stays the action button). No third treatment — destructive is the dashboard's
 * existing status semantic (the pre-C1 inline-error pattern), not a new palette.
 */
const QUIET_BOX = "rounded-lg border border-[hsl(var(--status-success))]/20 bg-[hsl(var(--status-success-bg))]/10 p-4";
const ACTION_BOX = "rounded-lg border border-destructive/20 bg-destructive/5 p-4";

/**
 * Feedforward grant-access affordance (doc 61 §C2): states WHERE the action happens
 * (Google's admin) and links there — labeled with destination + "(opens in a new tab)",
 * styled as a link, never a second CTA button (one primary action per step).
 */
const grantAccessLink = (
	<a
		href={GSC_ADMIN_URL}
		target="_blank"
		rel="noreferrer"
		className="text-coral hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
	>
		Open Search Console (opens in a new tab)
	</a>
);

function getApiErrorMessage(error: unknown, fallback: string): string {
	return (
		(error as { response?: { data?: { detail?: string } } })?.response?.data
			?.detail ||
		(error as Error)?.message ||
		fallback
	);
}

function formatGscSite(siteUrl: string): { display: string; badge: string } {
	if (siteUrl.startsWith("sc-domain:")) {
		return { display: siteUrl.slice("sc-domain:".length), badge: "Domain property" };
	}
	return { display: siteUrl, badge: "URL prefix" };
}

function bareHost(siteUrl: string): string {
	if (siteUrl.startsWith("sc-domain:")) {
		return siteUrl.slice("sc-domain:".length).toLowerCase();
	}
	try {
		return new URL(siteUrl).hostname.replace(/^www\./, "").toLowerCase();
	} catch {
		return siteUrl.toLowerCase();
	}
}

function sortGscSites(sites: GscSite[]): GscSite[] {
	return [...sites].sort((a, b) => {
		const aDomain = a.site_url.startsWith("sc-domain:");
		const bDomain = b.site_url.startsWith("sc-domain:");
		if (aDomain !== bDomain) return aDomain ? -1 : 1;
		return a.site_url.localeCompare(b.site_url);
	});
}

function hasUrlPrefixVariant(scDomainUrl: string, allSites: GscSite[]): boolean {
	const bare = bareHost(scDomainUrl);
	return allSites.some(
		(s) => !s.site_url.startsWith("sc-domain:") && bareHost(s.site_url) === bare,
	);
}

function formatLagDate(): string {
	const d = new Date(Date.now() - GSC_LAG_DAYS * 24 * 60 * 60 * 1000);
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Synced coverage is short of the 90-day target (doc 20 §2.4 lag + grace). Called only
 * for non-running statuses — selectConnectedBody gates `running` first, so the former
 * in-progress early-return was dead code and is removed. Short when the synced end date
 * is stale OR the synced day-count is below target — a backfill that covered e.g. 47 of
 * 90 days with a current end date is still partial (no false "completed").
 */
function isCoverageShort(syncStatus: GscSyncStatus): boolean {
	if (!syncStatus.date_range_end) return true;
	const endMs = Date.parse(syncStatus.date_range_end);
	if (Number.isNaN(endMs)) return true;
	const lagWindowMs = (GSC_LAG_DAYS + COVERAGE_GRACE_DAYS) * 24 * 60 * 60 * 1000;
	if (endMs < Date.now() - lagWindowMs) return true;
	// Short by day-count: fewer than the 90-day target (minus grace) is partial even
	// when the end date is current.
	const days = syncedDayCount(syncStatus);
	return days != null && days < BACKFILL_TARGET_DAYS - COVERAGE_GRACE_DAYS;
}

/** Whole days covered by the synced range — for "N of 90 days" (never a %). */
function syncedDayCount(syncStatus: GscSyncStatus): number | null {
	if (!syncStatus.date_range_start || !syncStatus.date_range_end) return null;
	const startMs = Date.parse(syncStatus.date_range_start);
	const endMs = Date.parse(syncStatus.date_range_end);
	if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return null;
	return Math.floor((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

function formatShortDate(iso: string | null | undefined): string {
	if (!iso) return "—";
	const ms = Date.parse(iso);
	if (Number.isNaN(ms)) return "—";
	return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatShortTime(iso: string | null | undefined): string {
	if (!iso) return "—";
	const ms = Date.parse(iso);
	if (Number.isNaN(ms)) return "—";
	return new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/**
 * Activation-spectrum body for the sync-status watch (doc 15 §13 / doc 61 §C1).
 * C1.5/(d)6: an HTTP failure or `never_synced` degrades to the pre-C1 fallback —
 * never a crash, never a false "failed" body.
 */
type ConnectedBody =
	| "loading"
	| "running"
	| "partial"
	| "failed"
	| "completed"
	| "fallback";

function selectConnectedBody(
	syncStatus: GscSyncStatus | undefined,
	isLoading: boolean,
	isError: boolean,
): ConnectedBody {
	if (isLoading) return "loading";
	// Graceful degradation: query HTTP failure OR never_synced → pre-C1 fallback.
	if (isError || !syncStatus || syncStatus.status === "never_synced") return "fallback";
	if (syncStatus.status === "failed") return "failed";
	if (SYNC_IN_PROGRESS_STATUSES.has(syncStatus.status)) return "running";
	return isCoverageShort(syncStatus) ? "partial" : "completed";
}

export default function ConnectSearchConsolePage() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();
	const tenantScope = currentTenant ?? "no-tenant";

	const [step, setStep] = useState<Step>("intro");
	const [selectedSite, setSelectedSite] = useState<string | null>(null);
	const [connecting, setConnecting] = useState(false);
	const [connectPhase, setConnectPhase] = useState<"saving" | "syncing" | null>(
		null,
	);
	const [connectError, setConnectError] = useState<string | null>(null);
	const [syncRetrying, setSyncRetrying] = useState(false);

	// --- Already-connected guard (Fix 3) ---
	const { data: settings } = useQuery({
		queryKey: ["connect-gsc-settings", tenantScope],
		queryFn: () => fetchTenantConnectSettings(currentTenant!),
		enabled: !!currentTenant,
		staleTime: 30 * 1000,
	});

	useEffect(() => {
		if (settings?.gsc_site_url && settings?.gsc_enabled && step === "intro") {
			setStep("already_connected");
		}
	}, [settings, step]);

	// --- Discovery ---
	const { data: sitesData, isLoading, isError, error, refetch } = useQuery({
		queryKey: ["connect-gsc-sites", tenantScope],
		queryFn: () => fetchGscSites(currentTenant!),
		enabled: step === "picker" && !!currentTenant,
		retry: false,
		staleTime: 0,
	});

	const sites = sitesData?.sites ?? [];
	const discoveryError = sitesData?.error;
	const sortedSites = sortGscSites(sites);

	// --- Sync status: drives the already-connected + success activation spectrum ---
	const watchSyncStatus = step === "already_connected" || step === "success";
	const {
		data: syncStatus,
		isLoading: syncStatusLoading,
		isError: syncStatusError,
		dataUpdatedAt,
	} = useQuery({
		queryKey: ["search-performance", tenantScope, "sync-status"],
		queryFn: () => fetchGscSyncStatus(currentTenant!),
		enabled: watchSyncStatus && !!currentTenant,
		staleTime: 30 * 1000,
		// Doc 61 §C1 / Norman: auto-poll ~30s ONLY while running; cadence shown visibly.
		refetchInterval: (query) =>
			query.state.data?.status === "running" ? SYNC_POLL_INTERVAL_MS : false,
	});
	const connectedBody = selectConnectedBody(
		syncStatus,
		syncStatusLoading,
		syncStatusError,
	);

	// Live re-check countdown for the running body (G2 / Norman: the polling cadence is
	// shown visibly — an indicator paired with a model, not a bare spinner). Resets on
	// every poll via dataUpdatedAt; ticks only while the running body is on screen.
	const showingRunningBody =
		connectedBody === "running" ||
		(step === "success" &&
			(connectedBody === "loading" || connectedBody === "fallback"));
	const [recheckIn, setRecheckIn] = useState(SYNC_POLL_INTERVAL_MS / 1000);
	useEffect(() => {
		if (!showingRunningBody) return;
		setRecheckIn(SYNC_POLL_INTERVAL_MS / 1000);
		const id = setInterval(
			() => setRecheckIn((s) => (s > 1 ? s - 1 : SYNC_POLL_INTERVAL_MS / 1000)),
			1000,
		);
		return () => clearInterval(id);
	}, [showingRunningBody, dataUpdatedAt]);

	// --- Connect (two-phase: save → sync) ---
	const handleConnect = async () => {
		if (!selectedSite || !currentTenant || connecting) return;
		setConnecting(true);
		setConnectError(null);
		setConnectPhase("saving");
		try {
			await saveGscConnection(currentTenant, {
				gsc_site_url: selectedSite,
				gsc_enabled: true,
			});
		} catch (err) {
			setConnectError(getApiErrorMessage(err, "Couldn't save connection."));
			setConnecting(false);
			setConnectPhase(null);
			return;
		}
		// Save succeeded — C1.4/G4: refresh the already-connected guard so it reflects
		// the new property immediately (settings query carries a 30s staleTime), then
		// sync (Fix 2: half-state if this fails).
		queryClient.invalidateQueries({
			queryKey: ["connect-gsc-settings", tenantScope],
		});
		setConnectPhase("syncing");
		try {
			await triggerGscBackfill(currentTenant);
			// Prefix-invalidates ["search-performance", tenant, "sync-status"] too, so the
			// success watch refetches and flips from "running" to the observed outcome.
			queryClient.invalidateQueries({ queryKey: ["search-performance", tenantScope] });
			setStep("success");
		} catch (err) {
			setConnectError(getApiErrorMessage(err, "Initial sync didn't start."));
			setStep("backfill_failed");
		} finally {
			setConnecting(false);
			setConnectPhase(null);
		}
	};

	// --- Backfill-only retry (half-state recovery + already-connected re-sync) ---
	const handleBackfillOnly = async () => {
		if (!currentTenant || syncRetrying) return;
		setSyncRetrying(true);
		setConnectError(null);
		try {
			await triggerGscBackfill(currentTenant);
			queryClient.invalidateQueries({ queryKey: ["search-performance", tenantScope] });
			setStep("success");
		} catch (err) {
			setConnectError(getApiErrorMessage(err, "Sync didn't start — try again."));
		} finally {
			setSyncRetrying(false);
		}
	};

	// --- No tenant selected ---
	if (!currentTenant) {
		return (
			<div className="mx-auto max-w-2xl space-y-6 p-6">
				<h1 className="text-dash-page font-semibold text-foreground">
					Connect Search Console
				</h1>
				<IntegrationStatePanel
					state="empty"
					message="Select a workspace to continue."
				/>
			</div>
		);
	}

	const currentStepNum = STEP_NUMBERS[step];
	const lagDate = formatLagDate();
	const dayCount = syncStatus ? syncedDayCount(syncStatus) : null;
	const isAuthFailure = GSC_AUTH_ERROR_PATTERN.test(syncStatus?.error_message ?? "");

	const goToPicker = () => {
		setSelectedSite(null);
		setConnectError(null);
		setStep("picker");
	};

	// --- Spectrum bodies (doc 61 §C1; two state-box patterns only) ---

	// running → QUIET box, no action; live re-check countdown + underpredicted ETA.
	const runningBody = (
		<div className={QUIET_BOX}>
			<div className="flex items-center gap-2">
				<span className="relative flex h-2 w-2" aria-hidden="true">
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--status-success))] opacity-60" />
					<span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--status-success))]" />
				</span>
				<span className="text-dash-label uppercase tracking-wider text-muted-foreground">
					Syncing
				</span>
			</div>
			<p className="mt-1 text-sm font-medium text-foreground">
				Sync in progress — data appears as it loads.
			</p>
			<div className="mt-2 flex items-start gap-2 text-dash-context text-muted-foreground">
				<Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
				<span>
					{syncStatus?.sync_started_at
						? `Started ${formatShortTime(syncStatus.sync_started_at)} · `
						: ""}
					up to {BACKFILL_TARGET_DAYS} days · Search Console lags ~{GSC_LAG_DAYS}{" "}
					days, recent days appear from {lagDate} · re-checking in {recheckIn}s
				</span>
			</div>
		</div>
	);

	// partial → QUIET box ("connected, incompletely"); one non-destructive outline action.
	const partialBody = (
		<div className={QUIET_BOX}>
			<span className="text-dash-label uppercase tracking-wider text-muted-foreground">
				Connected
			</span>
			<p className="mt-1 text-sm text-foreground">
				Connected — synced {dayCount ?? "…"} of {BACKFILL_TARGET_DAYS} days, data
				through {formatShortDate(syncStatus?.date_range_end)}.
			</p>
			{syncStatus?.rows_synced != null && (
				<p className="mt-1 text-dash-context text-muted-foreground">
					{syncStatus.rows_synced.toLocaleString()} rows synced.
				</p>
			)}
			<div className="mt-4">
				<Button
					variant="outline"
					onClick={handleBackfillOnly}
					disabled={syncRetrying}
					className="gap-1.5"
				>
					{syncRetrying ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" />
							Starting sync…
						</>
					) : (
						`Resume ${BACKFILL_TARGET_DAYS}-day sync`
					)}
				</Button>
			</div>
		</div>
	);

	// failed → ACTION box ("needs me"); blame-free headline, demoted raw detail, primary action.
	const failedBody = (
		<div className={ACTION_BOX}>
			<div className="flex items-start gap-2">
				<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-foreground">
						We couldn&rsquo;t finish syncing your Search Console data — your
						connection is fine.
					</p>
					{syncStatus?.error_message && (
						<p className="mt-1 break-words text-dash-context text-muted-foreground">
							Details: {syncStatus.error_message}
						</p>
					)}
				</div>
			</div>
			<div className="mt-4">
				{isAuthFailure ? (
					<Button onClick={goToPicker}>Reconnect Search Console</Button>
				) : (
					<Button
						onClick={handleBackfillOnly}
						disabled={syncRetrying}
						className="gap-1.5"
					>
						{syncRetrying ? (
							<>
								<Loader2 className="h-4 w-4 animate-spin" />
								Starting sync…
							</>
						) : (
							"Retry sync"
						)}
					</Button>
				)}
			</div>
		</div>
	);

	// completed → QUIET box mirroring the success step: state + lag date + one exit link only.
	const completedBody = (
		<div className={QUIET_BOX}>
			<p className="text-sm text-foreground">Connected. Data through {lagDate}.</p>
			<div className="mt-4 flex justify-end">
				<Button asChild>
					<Link to="/dashboard">Back to Dashboard</Link>
				</Button>
			</div>
		</div>
	);

	// C1.5/(d)6 fallback → the pre-C1 body (property + re-run), quiet "nothing to do" box.
	const preC1Body = (
		<>
			<div className={QUIET_BOX}>
				<span className="text-dash-label uppercase tracking-wider text-muted-foreground">
					Already connected
				</span>
				<p className="mt-1 break-all font-mono text-sm text-foreground">
					{settings?.gsc_site_url}
				</p>
			</div>
			{connectError && (
				<p className="break-words text-dash-context text-muted-foreground">
					{connectError}
				</p>
			)}
			<div className="flex items-center gap-2">
				<Button
					onClick={handleBackfillOnly}
					disabled={syncRetrying}
					className="gap-1.5"
				>
					{syncRetrying ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" />
							Starting sync…
						</>
					) : (
						`Re-run ${BACKFILL_TARGET_DAYS}-day sync`
					)}
				</Button>
				<Button variant="outline" onClick={goToPicker}>
					Choose a different property
				</Button>
			</div>
		</>
	);

	const renderSpectrumBody = (body: ConnectedBody) => {
		switch (body) {
			case "running":
				return runningBody;
			case "partial":
				return partialBody;
			case "failed":
				return failedBody;
			case "completed":
				return completedBody;
			default:
				return null;
		}
	};

	return (
		<div className="mx-auto max-w-2xl space-y-6 p-6">
			<div>
				<Link
					to="/dashboard"
					className="text-dash-context text-muted-foreground hover:text-foreground"
				>
					← Dashboard
				</Link>
				<h1 className="mt-2 text-dash-page font-semibold text-foreground">
					Connect Search Console
				</h1>
			</div>

			<Card className="p-6">
				{currentStepNum && (
					<p className="mb-4 text-dash-context text-muted-foreground">
						Step {currentStepNum} of 3
					</p>
				)}

				{step === "intro" && (
					<div className="rounded-lg border border-dashed border-warm-border-strong bg-warm-bg px-8 py-9 text-center">
						<p className="text-dash-title font-semibold text-ink">
							See what earns attention in search.
						</p>
						<div className="mx-auto mt-2 max-w-[460px] text-dash-subtitle text-ink-2">
							<p>
								Connect Search Console to see organic clicks, top pages, and what
								your content earns.{" "}
								<strong className="text-foreground">Before you continue:</strong>{" "}
								Grant access to the agency&rsquo;s Google account in Search Console,
								then come back here.
							</p>
							<div className="mt-2 text-dash-context">{grantAccessLink}</div>
						</div>
						<div className="mt-5">
							<Button
								onClick={() => setStep("picker")}
								className="min-h-11 bg-coral text-primary-foreground hover:bg-coral-hover"
							>
								Continue
							</Button>
						</div>
						<p className="mt-3.5 text-dash-context text-ink-3">
							Setup usually takes about a minute. First data typically appears
							within a day, then Search Console lags ~3 days.
						</p>
					</div>
				)}

				{step === "already_connected" && (
					<div className="space-y-4">
						{connectedBody === "loading" && (
							<Skeleton className="h-28 rounded-lg" />
						)}
						{connectedBody === "fallback" && preC1Body}
						{renderSpectrumBody(connectedBody)}
					</div>
				)}

				{step === "picker" && (
					<div className="space-y-4">
						<h2 className="text-dash-title font-semibold text-foreground">
							Select your property
						</h2>

						{isLoading ? (
							<div className="space-y-2">
								{Array.from({ length: 3 }).map((_, i) => (
									<Skeleton key={i} className="h-14 rounded-lg" />
								))}
							</div>
						) : isError ? (
							<IntegrationStatePanel
								state="error"
								errorReason={getApiErrorMessage(
									error,
									"Couldn't load properties — please try again.",
								)}
								onRetry={() => refetch()}
							/>
						) : discoveryError ? (
							// Fix 6: OAuth unconfigured — non-retryable
							<div className="rounded-lg border p-4">
								<p className="text-sm text-foreground">
									Agency Google connection isn&rsquo;t configured — contact the
									AgileCo team.
								</p>
							</div>
						) : sites.length === 0 ? (
							<div className="space-y-4">
								<div className="rounded-lg border border-dashed border-warm-border-strong bg-warm-bg px-8 py-9 text-center">
									<p className="text-dash-title font-semibold text-ink">
										We can&rsquo;t see your property yet.
									</p>
									<div className="mx-auto mt-2 max-w-[460px] text-dash-subtitle text-ink-2">
										<p>
											Make sure the agency&rsquo;s Google account has been granted
											access to your property in Search Console, then re-check.
										</p>
										<div className="mt-2 text-dash-context">{grantAccessLink}</div>
									</div>
									<Button
										onClick={() => refetch()}
										className="mt-5 min-h-11 gap-1.5 bg-coral text-primary-foreground hover:bg-coral-hover"
									>
										<RefreshCw className="h-4 w-4" />
										Re-check
									</Button>
									<p className="mt-3.5 text-dash-context text-ink-3">
										Access usually shows up within a few minutes of granting.
									</p>
								</div>
								<div>
									<Button variant="ghost" size="sm" onClick={() => setStep("intro")}>
										Back
									</Button>
								</div>
							</div>
						) : (
							<>
								{/* Fix 1: sc-domain vs URL-prefix explainer */}
								<p className="text-dash-context text-muted-foreground">
									Domain property covers all URLs including subdomains; URL prefix
									covers only that exact address.
								</p>
								<div role="radiogroup" aria-label="Search Console property" className="space-y-2">
									{sortedSites.map((site: GscSite) => {
										const { display, badge } = formatGscSite(site.site_url);
										const isScDomain = site.site_url.startsWith("sc-domain:");
										const showRecommended =
											isScDomain && hasUrlPrefixVariant(site.site_url, sites);
										return (
											<label
												key={site.site_url}
												className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted"
											>
												<input
													type="radio"
													name="gsc-site"
													value={site.site_url}
													checked={selectedSite === site.site_url}
													onChange={() => setSelectedSite(site.site_url)}
													className="h-4 w-4 accent-coral"
												/>
												<span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
													{display}
												</span>
												{showRecommended && (
													<Badge variant="secondary">Recommended</Badge>
												)}
												<Badge variant="outline">{badge}</Badge>
											</label>
										);
									})}
								</div>
								<div className="flex items-center justify-between">
									<Button variant="ghost" size="sm" onClick={() => setStep("intro")}>
										Back
									</Button>
									<Button
										onClick={() => setStep("confirm")}
										disabled={!selectedSite}
									>
										Continue
									</Button>
								</div>
							</>
						)}
					</div>
				)}

				{step === "confirm" && selectedSite && (
					<div className="space-y-4">
						<h2 className="text-dash-title font-semibold text-foreground">
							Confirm connection
						</h2>
						<div className="rounded-lg border p-4">
							<span className="text-dash-label uppercase tracking-wider text-muted-foreground">
								Property
							</span>
							<p className="mt-1 break-all font-mono text-sm text-foreground">
								{selectedSite}
							</p>
						</div>
						<p className="text-dash-context text-muted-foreground">
							We&rsquo;ll save this property and start a {BACKFILL_TARGET_DAYS}
							-day backfill. Search Console data lags ~{GSC_LAG_DAYS} days.
						</p>

						{/* Fix 7: inline retry — action box ("needs me"), raw detail demoted. */}
						{connectError && (
							<div className={ACTION_BOX}>
								<div className="flex items-start gap-2">
									<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
									<div className="min-w-0 flex-1">
										<p className="text-sm font-medium text-foreground">
											Couldn&rsquo;t connect
										</p>
										<p className="mt-0.5 break-words text-dash-context text-muted-foreground">
											Details: {connectError}
										</p>
									</div>
								</div>
								<div className="mt-3 flex justify-end">
									<Button
										variant="outline"
										size="sm"
										onClick={handleConnect}
										disabled={connecting}
									>
										Retry
									</Button>
								</div>
							</div>
						)}

						<div className="flex items-center justify-between">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setStep("picker");
									setConnectError(null);
								}}
								disabled={connecting}
							>
								Back
							</Button>
							<Button
								onClick={handleConnect}
								disabled={connecting}
								className="gap-1.5"
							>
								{connecting ? (
									<>
										<Loader2 className="h-4 w-4 animate-spin" />
										{/* Fix 4: two-phase label */}
										{connectPhase === "saving"
											? "Saving connection…"
											: `Starting ${BACKFILL_TARGET_DAYS}-day sync…`}
									</>
								) : (
									`Connect & sync ${BACKFILL_TARGET_DAYS} days`
								)}
							</Button>
						</div>
					</div>
				)}

				{/* Fix 2: half-state — save ok, backfill trigger failed (action box: needs me). */}
				{step === "backfill_failed" && (
					<div className="space-y-4">
						<div className={ACTION_BOX}>
							<div className="flex items-start gap-2">
								<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
								<div className="min-w-0 flex-1">
									<p className="text-sm font-medium text-foreground">
										Connected — initial sync didn&rsquo;t start. Your connection is
										saved.
									</p>
									{connectError && (
										<p className="mt-1 break-words text-dash-context text-muted-foreground">
											Details: {connectError}
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="flex items-center justify-between">
							<Button asChild variant="ghost" size="sm">
								<Link to="/dashboard">Back to Dashboard</Link>
							</Button>
							<Button
								onClick={handleBackfillOnly}
								disabled={syncRetrying}
								className="gap-1.5"
							>
								{syncRetrying ? (
									<>
										<Loader2 className="h-4 w-4 animate-spin" />
										Starting sync…
									</>
								) : (
									"Start sync"
								)}
							</Button>
						</div>
					</div>
				)}

				{/* Post-success watch (doc 61 §C1 (c)3): flip from "running" to the observed
				    outcome — never leave a lying success on screen. Loading/fallback show the
				    honest running watch right after a sync is triggered. */}
				{step === "success" && (
					<div className="space-y-4">
						{connectedBody === "partial" ||
						connectedBody === "failed" ||
						connectedBody === "completed"
							? renderSpectrumBody(connectedBody)
							: runningBody}
					</div>
				)}
			</Card>
		</div>
	);
}
